import { Plus, XSquare } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import FormModelComponent from '../../../../../components/model/FormModelComponent';
import { useEffect, useState, useCallback } from 'react';
import { useForm } from 'antd/es/form/Form';
import AddTaskForm from './AddTaskForm';

import ConfirmModelComponent from '../../../../../components/model/ConfirmationModelComponent';
import { referenceDataSyncService } from '../../../../../services/referenceDataSyncService';
import { useNotificationContext } from '../../../../../contexts/NotificationContext';
import { createTask, updateTask } from '../../../../../api/services/taskService';
import { useLocalData } from '../../../../../hooks/useLocalData';
import dayjs from 'dayjs';
import { trimValues } from '../../../../../utils/trimValues';
import { getInspectorUsers } from '../../../../../api/services/userService';
import PrimarySquareBtn from '../../../../../components/buttons/PrimarySquareBtn';
import { LegalMetroTaskTypeEnum, TaskTypeEnum } from '../../../../../enums/TaskTypeEnum';
import { OccurrenceUnit } from '../../../../../enums/OccurenceUnitEnum';
import { useIsOnlineContext } from '../../../../../contexts/IsOnlineContext';
import { RoleName } from '../../../../../enums/RoleNameEnm';

interface ITask {
  taskName: string;
  assignee: string;
  priority: string;
  market: string;
  typeOfMarket: string;
  category: string;
  dueDate: string;
  description: string;
}

const initialTask = {
  taskName: '',
  assignee: '',
  priority: '',
  market: '',
  typeOfMarket: '',
  category: '',
  dueDate: '',
  description: '',
};

interface CreateNewTaskProps {
  refreshTasks?: () => void;
  selectedTaskId?: string;
  selectedTaskData?: any;
  isEditClicked?: boolean;
  triggerEdit?: number;
  hideCreateBtn?: boolean;
}

const CreateNewTask: React.FC<CreateNewTaskProps> = ({
  refreshTasks,
  selectedTaskId,
  isEditClicked,
  triggerEdit,
  hideCreateBtn,
}) => {
  const { t } = useTranslation('task');

  const [createTaskForm] = useForm();
  const [newTask, setNewTask] = useState<ITask>(initialTask);

  const [isCreateTaskModalOpen, setIsCreateTaskModalOpen] = useState<boolean>(false);

  const [isConfirmationOpen, setIsConfirmationOpen] = useState<boolean>(false);

  const [isEdit, setIsEdit] = useState<boolean>(false);

  const { isOnline } = useIsOnlineContext();

  const { successNotification, errorNotification } = useNotificationContext();
  const { updateLocalData } = useLocalData({
    entity: 'task',
    autoSync: false,
    sortBy: 'name',
    sortOrder: 'asc',
  });

  const handleNewCreateTask = () => {
    setIsCreateTaskModalOpen(true);
  };

  const closeCreateTaskModal = () => {
    createTaskForm.resetFields();
    setNewTask(initialTask);
    setIsCreateTaskModalOpen(false);
    setIsConfirmationOpen(false);
  };

  const getConfirmation = async () => {
    await createTaskForm.validateFields();
    setIsConfirmationOpen(true);
  };

  const handleCancel = () => {
    createTaskForm.resetFields();
    setNewTask(initialTask);
    setIsCreateTaskModalOpen(false);
  };

  const handleCreateTask = async () => {
    const createTaskFormData = createTaskForm.getFieldsValue();

    const taskTypeSelected = createTaskFormData?.taskType;
    const legalMetroSubTypeSelected = createTaskFormData?.legalMetroSubTypes;
    const trimmedValues = trimValues(createTaskFormData);
    createTaskForm.setFieldsValue(trimmedValues);

    setNewTask(createTaskFormData);

    let establishmentId =
      legalMetroSubTypeSelected !== LegalMetroTaskTypeEnum.FUEL_DISPENSER_VERIFICATION
        ? await referenceDataSyncService.getEstablishmentsId(
            trimmedValues?.market || trimmedValues?.typeOfEstablishment,
            trimmedValues?.typeOfMarket || trimmedValues?.establishment
          )
        : undefined;

    const selectedLocationId = await referenceDataSyncService.getLocationId(
      trimmedValues?.department || trimmedValues?.departmentLegal,
      trimmedValues?.municipality || trimmedValues?.municipalityLegal
    );

    const isRecurrent =
      createTaskFormData?.recurrent || createTaskFormData?.isTaskRepeated || false;

    const unitMap: Record<OccurrenceUnit, string> = {
      [OccurrenceUnit.Weeks]: 'WEEKLY',
      [OccurrenceUnit.Months]: 'MONTHLY',
      [OccurrenceUnit.Quarters]: 'QUARTERLY',
      [OccurrenceUnit.Years]: 'YEARLY',
      [OccurrenceUnit.Days]: 'DAILY',
    };

    let bodyBasedOnTaskType;
    if (taskTypeSelected === TaskTypeEnum.LEGAL_METROLOGY_INSPECTION) {
      const baseBodyForLegal = {
        taskTypeId: trimmedValues?.taskTypeId,
        subTaskTypeId: trimmedValues?.subTaskTypeId,
        taskName: trimmedValues?.taskNameLegal,
        assignees: trimmedValues?.assigneeIds,
        priority: trimmedValues?.priorityLegal,
        locationId: selectedLocationId,
        dueDate: trimmedValues?.dueDateLegal.valueOf(),
        description: trimmedValues?.descriptionLegal,
        isActive: true,
        isRecurring: isRecurrent || false,
        recurrenceEndDate: isRecurrent ? createTaskFormData.occursUntilLegal.valueOf() : null,
        recurrenceFrequency: isRecurrent
          ? unitMap[createTaskFormData.occursEveryUnitLegal as OccurrenceUnit]
          : null,
        recurrenceInterval: isRecurrent ? createTaskFormData.occursEveryNumberLegal : null,
      };
      switch (legalMetroSubTypeSelected) {
        case LegalMetroTaskTypeEnum.SCALE_FORMAT_VERIFICATION:
          bodyBasedOnTaskType = {
            ...baseBodyForLegal,
            establishmentId: establishmentId && (establishmentId[0]?.id as number),
          };
          break;
        case LegalMetroTaskTypeEnum.PRE_PACKAGED_PRODUCT_VERIFICATION:
          bodyBasedOnTaskType = {
            ...baseBodyForLegal,
            productRefIds: Array.of(trimmedValues?.productRefId),
            basketIds: trimmedValues?.basketIdsLegal,
            productCategoryIds: trimmedValues?.productCategoryIdsLegal,
            establishmentId: establishmentId && (establishmentId[0]?.id as number),
          };
          break;
        case LegalMetroTaskTypeEnum.FUEL_DISPENSER_VERIFICATION:
          bodyBasedOnTaskType = {
            ...baseBodyForLegal,
            fuelStationName: trimmedValues?.fuelStationName,
          };
          break;
        default:
          bodyBasedOnTaskType = {
            ...baseBodyForLegal,
          };
          break;
      }
    } else {
      //CONSUMER_PRICE_INSPECTION task type
      bodyBasedOnTaskType = {
        taskTypeId: trimmedValues?.taskTypeId,
        taskName: trimmedValues?.taskName,
        assignees: trimmedValues?.assigneeIds,
        priority: trimmedValues?.priority,
        locationId: selectedLocationId,
        establishmentId: establishmentId && (establishmentId[0]?.id as number),
        basketIds: trimmedValues?.basketIds,
        productCategoryIds: trimmedValues?.productCategoryIds,
        productRefIds: trimmedValues?.productRefIds,
        category: trimmedValues?.category,
        dueDate: trimmedValues?.dueDate.valueOf(),
        description: trimmedValues?.description,
        isActive: true,
        isRecurring: isRecurrent || false,
        recurrenceEndDate: isRecurrent ? createTaskFormData.occursUntil.valueOf() : null,
        recurrenceFrequency: isRecurrent
          ? unitMap[createTaskFormData.occursEveryUnit as OccurrenceUnit]
          : null,
        recurrenceInterval: isRecurrent ? createTaskFormData.occursEveryNumber : null,
      };
    }

    bodyBasedOnTaskType = isEdit
      ? bodyBasedOnTaskType
      : {
          ...bodyBasedOnTaskType,
          createTime: Date.now(),
          updateTime: Date.now(),
        };

    setIsConfirmationOpen(false);
    setIsCreateTaskModalOpen(false);

    if (isEdit) {
      try {
        if (!selectedTaskId) {
          throw new Error('Task ID is required for update');
        }

        await updateTask(selectedTaskId!, bodyBasedOnTaskType as any);
        if (refreshTasks) {
          refreshTasks();
        }
        setNewTask(initialTask);
        createTaskForm.resetFields();
        successNotification(t('taskUpdateSuccess'));
        setIsConfirmationOpen(false);
        setIsCreateTaskModalOpen(false);
        // refreshTasks?.();
      } catch (error) {
        console.error('Failed to update task:', error);
        setIsCreateTaskModalOpen(false);
        setIsConfirmationOpen(false);
        errorNotification(t('taskUpdateFail'));
      }
    } else {
      try {
        // Step 1: Call backend API for immediate persistence
        const result = await createTask(bodyBasedOnTaskType as any);
        const taskWithRefId = { ...result, refId: result.refId || result.id };
        updateLocalData((prevTasks) => [taskWithRefId, ...prevTasks]);

        if (refreshTasks) {
          refreshTasks();
        }

        setNewTask(initialTask);
        createTaskForm.resetFields();
        successNotification(t('taskCreateSuccess'));
        setIsConfirmationOpen(false);
        setIsCreateTaskModalOpen(false);
      } catch (error) {
        createTaskForm.resetFields();
        setIsCreateTaskModalOpen(false);
        setIsConfirmationOpen(false);
        errorNotification(t('taskCreateFail'));
      }
    }
  };

  const cancelConfirmation = () => {
    setIsConfirmationOpen(false);
  };

  const sessionKey = isEdit ? `edit-${selectedTaskId}` : 'create';

  const handleEdit = useCallback(async () => {
    setIsCreateTaskModalOpen(true);

    try {
      const taskData = await referenceDataSyncService.getTaskById(selectedTaskId!);

      const dueDateDayJs = dayjs(Number(taskData?.dueDate));
      if (taskData?.basketIds && Array.isArray(taskData.basketIds)) {
        const basketNames = [];
        for (const basketId of taskData.basketIds) {
          const basketDataArray = await referenceDataSyncService.getBasketDataById(basketId);
          const basket = basketDataArray[0];
          if (basket?.data && typeof basket.data === 'object' && 'name' in basket.data) {
            basketNames.push((basket.data as any).name);
          }
        }
        createTaskForm.setFieldValue('baskets', taskData.basketIds);
        createTaskForm.setFieldValue('basketIds', taskData.basketIds);
      } else if (taskData?.basketId) {
        createTaskForm.setFieldValue('baskets', [taskData.basketId]);
        createTaskForm.setFieldValue('basketIds', [taskData.basketId]);
      }

      /* Assignee IDs */
      let assigneeIds: string[] = [];
      try {
        if (taskData?.assignees) {
          assigneeIds =
            typeof taskData.assignees === 'string'
              ? JSON.parse(taskData.assignees)
              : taskData.assignees;
        }
      } catch (error) {
        console.warn('Failed to parse assignees:', error);
        assigneeIds = [];
      }

      /* Assignee Names */
      const users = await getInspectorUsers({
        roles: [RoleName.INSPECTOR, RoleName.AUDITOR],
      });
      if (users?.statusText === 'SUCCESS' && users?.data?.data) {
        const userData: any[] = users.data.data;

        const assigneeNames = assigneeIds.map((assigneeId: string) => {
          const user = userData.find((u: any) => u.id === assigneeId);
          return user ? user.userName : assigneeId;
        });
        setTimeout(() => {
          createTaskForm.setFieldValue(
            taskData?.taskTypeKey === TaskTypeEnum.LEGAL_METROLOGY_INSPECTION
              ? 'assigneeLegal'
              : 'assignee',
            assigneeNames
          );
        }, 100);
      } else {
        setTimeout(() => {
          createTaskForm.setFieldValue(
            taskData?.taskTypeKey === TaskTypeEnum.LEGAL_METROLOGY_INSPECTION
              ? 'assigneeLegal'
              : 'assignee',
            []
          );
        }, 100);
      }

      /* Department and municipality data */
      if (taskData?.locationId) {
        const locationData = await referenceDataSyncService.getLocationById(taskData?.locationId);
        if (locationData) {
          createTaskForm.setFieldValue(
            taskData?.taskTypeKey === TaskTypeEnum.LEGAL_METROLOGY_INSPECTION
              ? 'departmentLegal'
              : 'department',
            locationData?.district
          );
          createTaskForm.setFieldValue(
            taskData?.taskTypeKey === TaskTypeEnum.LEGAL_METROLOGY_INSPECTION
              ? 'municipalityLegal'
              : 'municipality',
            locationData?.city
          );
        }
      }

      createTaskForm.setFieldsValue({
        taskTypeId: taskData?.taskTypeId,
        assigneeIds: assigneeIds,
      });

      if (taskData?.taskTypeKey === TaskTypeEnum.LEGAL_METROLOGY_INSPECTION) {
        createTaskForm.setFieldsValue({
          taskType: TaskTypeEnum?.LEGAL_METROLOGY_INSPECTION,
          subTaskTypeId: taskData?.subTaskTypeId,
          taskNameLegal: taskData?.taskName,
          // assigneeLegal: taskData?.assignees,
          priorityLegal: taskData?.priority,
          dueDateLegal: dueDateDayJs,
          descriptionLegal: taskData?.description,
        });

        switch (taskData?.subTaskTypeKey) {
          case LegalMetroTaskTypeEnum.SCALE_FORMAT_VERIFICATION:
            createTaskForm.setFieldsValue({
              legalMetroSubTypes: LegalMetroTaskTypeEnum.SCALE_FORMAT_VERIFICATION,
              typeOfEstablishment: taskData?.establishmentType,
              establishment: taskData?.establishmentName,
            });
            break;
          case LegalMetroTaskTypeEnum.PRE_PACKAGED_PRODUCT_VERIFICATION:
            createTaskForm.setFieldsValue({
              legalMetroSubTypes: LegalMetroTaskTypeEnum.PRE_PACKAGED_PRODUCT_VERIFICATION,
              productLegal: taskData?.productRefIds && taskData?.productRefIds[0],
              typeOfEstablishment: taskData?.establishmentType,
              establishment: taskData?.establishmentName,
              productRefId: taskData?.productRefIds && taskData?.productRefIds[0],
            });

            if (taskData?.basketIds && Array.isArray(taskData.basketIds)) {
              createTaskForm.setFieldsValue({
                basketsLegal: taskData.basketIds,
                basketIdsLegal: taskData.basketIds,
              });
            }

            if (taskData?.productCategoryIds && Array.isArray(taskData.productCategoryIds)) {
              createTaskForm.setFieldsValue({
                productCategoriesLegal: taskData.productCategoryIds,
                productCategoryIdsLegal: taskData.productCategoryIds,
              });
            }
            break;
          case LegalMetroTaskTypeEnum.FUEL_DISPENSER_VERIFICATION:
            createTaskForm.setFieldsValue({
              legalMetroSubTypes: LegalMetroTaskTypeEnum.FUEL_DISPENSER_VERIFICATION,
              fuelStationName: taskData?.fuelStationName,
            });
            break;
          default:
            createTaskForm.setFieldValue(
              'legalMetroSubTypes',
              LegalMetroTaskTypeEnum.SCALE_FORMAT_VERIFICATION
            );
            break;
        }
      } else {
        createTaskForm.setFieldsValue({
          taskType: TaskTypeEnum?.CONSUMER_PRICE_INSPECTION,
          taskName: taskData?.taskName,
          priority: taskData?.priority,
          market: taskData?.establishmentType,
          typeOfMarket: taskData?.establishmentName,
          product: taskData?.productRefIds || [],
          productRefIds: taskData?.productRefIds || [],
          category: taskData?.category || [],
          dueDate: dueDateDayJs,
          description: taskData?.description,
        });

        if (taskData?.productCategoryIds && Array.isArray(taskData.productCategoryIds)) {
          createTaskForm.setFieldsValue({
            productCategories: taskData?.productCategoryIds,
            productCategoryIds: taskData?.productCategoryIds,
          });
        }
      }

      setIsEdit(true);
    } catch (error) {
      console.error('Failed to load task data:', error);
      errorNotification('Failed to load task data');
      setIsCreateTaskModalOpen(false);
    }
  }, [selectedTaskId, errorNotification, createTaskForm]);

  useEffect(() => {
    if (isEditClicked && selectedTaskId) {
      handleEdit();
    }
  }, [triggerEdit, isEditClicked, selectedTaskId, handleEdit]);

  const renderAddTaskForm = () => (
    <AddTaskForm
      key={sessionKey}
      form={createTaskForm}
      // task={newTask}
      getConfirmation={getConfirmation}
      isEdit={isEdit}
    />
  );

  return (
    <>
      <ConfirmModelComponent
        isOpen={isConfirmationOpen}
        onClickedCancel={cancelConfirmation}
        header={isEdit ? t('confirmUpdateTitle') : t('confirmCreateTitle')}
        content={<p>{isEdit ? t('confirmUpdateMessage') : t('confirmCreateMessage')}</p>}
        footerAction={{
          confimation: {
            label: t('confirm'),
            onClick: handleCreateTask,
          },
          cancel: {
            label: t('cancel'),
            onClick: cancelConfirmation,
          },
        }}
        styleClasses="z-100"
      />
      {/* <div className='hidden' aria-hidden="true">
        <AddTaskForm
            form={createTaskForm}
            // task={newTask}
            getConfirmation={getConfirmation}
            isEdit={false}
          />
      </div> */}

      <FormModelComponent
        isOpen={isCreateTaskModalOpen}
        header={isEdit ? t('updateTaskTitle') : t('createNewTaskTitle')}
        content={isCreateTaskModalOpen ? renderAddTaskForm() : []}
        /* content={
          <AddTaskForm
            form={createTaskForm}
            task={newTask}
            getConfirmation={getConfirmation}
            isEdit={isEdit}
          />
        } */
        onClickedCancel={closeCreateTaskModal}
        footerAction={{
          confimation: {
            label: `${isEdit ? t('updateTask') : t('createTask')}`,
            onClick: getConfirmation,
          },
          cancel: {
            label: t('cancel'),
            // label: t('back'),
            onClick: handleCancel,
          },
        }}
        closeIcon={<XSquare className="text-primary-font-red" size={20} />}
        contentStyles="mb-4 px-2 max-h-[80vh] overflow-y-auto space-y-2"
        styleClasses="create-task-modal"
      ></FormModelComponent>

      {!isEdit && !hideCreateBtn && (
        <div className="flex justify-end">
          <PrimarySquareBtn handleOnClick={handleNewCreateTask} disabled={!isOnline}>
            <div className="flex">
              <Plus className="h-5 w-5 text-white md:h-6 md:w-6" />
              <p className="pt-1">{t('createNewTask')}</p>
            </div>
          </PrimarySquareBtn>
        </div>
      )}

      <div>{/* <TaskLists /> */}</div>
    </>
  );
};

export default CreateNewTask;
